import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './i18n/config';
import { BrandProvider } from './brand/BrandProvider';
import AppThemeProvider from './components/AppThemeProvider';
import Login from './pages/Login';
import ScormLogin from './pages/ScormLogin';
import MagicLinkVerify from './pages/MagicLinkVerify';
import Dashboard from './pages/Dashboard';
import CourseDetail from './pages/CourseDetail';
import Quiz from './pages/Quiz';
import Capsule from './pages/Capsule';
import Workshop from './pages/Workshop';
import ChapterRating from './pages/ChapterRating';
import ChapterSummary from './pages/ChapterSummary';
import Assessment from './pages/Assessment';
import ReminderEmailPreferencesPage from './pages/ReminderEmailPreferencesPage';
import ProtectedRoute from './components/ProtectedRoute';
import CourseShell from './components/CourseShell';
import LearningLayout from './components/LearningLayout';
import ChatWidget from './components/ChatWidget';
import PublicChat from './pages/PublicChat';

/** Le chat public est embarqué en iframe sur des sites tiers : pas de widget par-dessus. */
const PUBLIC_EMBED_PATHS = ['/chat'];

const AppChatWidget: React.FC = () => {
  const { pathname } = useLocation();
  if (PUBLIC_EMBED_PATHS.includes(pathname)) return null;
  return <ChatWidget />;
};

function App() {
  return (
    <Router>
      <BrandProvider>
        <AppThemeProvider>
          <Routes>
            <Route path="/chat" element={<PublicChat />} />
            <Route path="/login" element={<Login />} />
            <Route path="/scorm-login" element={<ScormLogin />} />
            <Route path="/login/magic-link/:token" element={<MagicLinkVerify />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <ReminderEmailPreferencesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/course/:courseId"
              element={
                <ProtectedRoute>
                  <CourseShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<CourseDetail />} />
              <Route element={<LearningLayout />}>
                <Route path="chapter/:chapterId/quiz/:quizId" element={<Quiz />} />
                <Route path="chapter/:chapterId/capsule/:capsuleId" element={<Capsule />} />
                <Route path="chapter/:chapterId/workshop/:workshopId" element={<Workshop />} />
                <Route path="chapter/:chapterId/quiz-order/:quizOrder" element={<Quiz />} />
                <Route path="chapter/:chapterId/capsule-order/:capsuleOrder" element={<Capsule />} />
                <Route path="chapter/:chapterId/workshop-order/:workshopOrder" element={<Workshop />} />
                <Route path="chapter/:chapterId/rating" element={<ChapterRating />} />
                <Route path="chapter/:chapterId/summary" element={<ChapterSummary />} />
                <Route
                  path="assessment/followup/:followupId"
                  element={<Assessment />}
                />
                <Route path="assessment/:measurement" element={<Assessment />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <AppChatWidget />
        </AppThemeProvider>
      </BrandProvider>
    </Router>
  );
}

export default App;
